import { useState, Fragment, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import * as yup from 'yup'
import qs from 'qs'
import {
  Container,
  Section,
  SectionTitle,
  Button,
  Alert,
  Inline,
  ListItem,
} from 'chaoskit/src/components'
import { generateGradient } from 'chaoskit/src/assets/styles/utility/gradient'
import axios from 'axios'
import to from 'await-to-js'
import { Formik, Form } from 'formik'
import { misc } from 'chaoskit/src/assets/styles/utility'
import FormikField from 'chaoskit/src/components/FormikField'
import useIntersection from 'react-use/lib/useIntersection'
import { motion, useAnimation } from 'framer-motion'

import {
  HomeHeroGrid,
  HomeHeroGridImage,
  HomeHeroGridContent,
} from '~partials/home/Hero'
import BaseLayout from '~layouts/BaseLayout'
import Hero, { HeroTitle, HeroTitleSub } from '~partials/Hero'
import Data from '~components/Data'
import HelmetSEO from '~partials/global/HelmetSEO'
import { ReactComponent as HeroSVG } from '~media/bootcamps/hero.svg'
import { ReactComponent as Bootcamp101SVG } from '~media/bootcamps/bootcamp101.svg'
import GradientList from '~partials/global/GradientList'
import { stickyIt } from '~styles/utility'
import LogosGrid from '~components/LogosGrid'
import { expediaObj, workivaObj, jpmcObj } from '~partials/global/CustomerLogos'
import conferenceAWSReinvent from '~media/logos/aws-reinvent.svg'
import conferenceSRECon from '~media/logos/sre-con.svg'
import conferenceQCon from '~media/logos/qcon.svg'
import conferenceChaosConf from '~media/logos/chaos-conf.svg'
import footer1 from '~media/bootcamps/footer-1.jpg'
import footer2 from '~media/bootcamps/footer-2.jpg'
import footer3 from '~media/bootcamps/footer-3.jpg'
import footer4 from '~media/bootcamps/footer-4.jpg'
import footer5 from '~media/bootcamps/footer-5.jpg'
import footer6 from '~media/bootcamps/footer-6.jpg'
import DateActionCard from '~components/DateActionCard'
import track from '~helpers/track'
import '~helpers/yup-corporate-email'
import BotField from '~components/BotField'

const BootcampGrid = (props) => {
  const theme = useTheme()

  return (
    <div
      css={[
        {
          display: 'grid',
          gap: theme.space.base,

          [theme.mq.medium]: {
            gap: theme.space.large,
            gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
          },
        },
      ]}
      {...props}
    />
  )
}

const BootcampGridLeft = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{
        [theme.mq.medium]: {
          gridColumn: '1 / span 6',
          gridRow: 1,
        },
      }}
      {...props}
    />
  )
}

const BootcampGridRight = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{
        [theme.mq.medium]: {
          gridColumn: '7 / span 6',
          gridRow: 1,
        },
      }}
      {...props}
    />
  )
}

const BootcampWorkshopsSection = ({ data }) => {
  const theme = useTheme()

  return (
    <Section
      css={{
        [theme.mq.medium]: {
          paddingBottom: '0 !important',
        },
      }}
    >
      <Container>
        <BootcampGrid
          css={{
            gap: 0,
          }}
        >
          <div
            css={{
              [theme.mq.small__max]: {
                textAlign: 'center',
              },

              [theme.mq.medium]: [
                {
                  gridColumn: '1 / span 7',
                  gridRow: 1,
                  direction: 'rtl',
                },
              ],
            }}
          >
            <Bootcamp101SVG
              css={{
                width: '100%',
                maxWidth: 400,

                [theme.mq.medium]: [
                  stickyIt(theme),
                  {
                    maxWidth: 'none',
                    width: 600,
                  },
                ],
              }}
            />
          </div>
          <BootcampGridRight
            css={{
              display: 'grid',
              gap: theme.space.large,
              position: 'relative',
              zIndex: 1,
            }}
          >
            {data.map((section) => (
              <div key={section.heading}>
                <SectionTitle
                  as="h2"
                  align={{ medium: 'left' }}
                  title={<Data>{section.heading}</Data>}
                  css={{
                    marginBottom: `${theme.space.large}px !important`,

                    'h2 span': {
                      fontSize: '150%',
                    },
                  }}
                  sub={<Data>{section.blurb}</Data>}
                />
                <GradientList
                  items={section.agenda}
                  css={{
                    '> li': {
                      lineHeight: theme.lineHeight.base,

                      '+ li::before': {
                        background: generateGradient({
                          start: theme.brand.darkPurple,
                          stop: theme.brand.purple,
                        }),
                      },
                    },
                  }}
                />
              </div>
            ))}
          </BootcampGridRight>
        </BootcampGrid>
      </Container>
    </Section>
  )
}

BootcampWorkshopsSection.propTypes = {
  data: PropTypes.array.isRequired,
}

const BootcampFindSection = ({ data }) => {
  const theme = useTheme()

  return (
    <Section id="find">
      <Container>
        <BootcampGrid>
          <BootcampGridLeft>
            <div
              css={{
                [theme.mq.medium]: [stickyIt(theme)],
              }}
            >
              <SectionTitle
                as="h2"
                align={{ medium: 'left' }}
                title={<Data>{data.heading}</Data>}
                css={{
                  marginBottom: `0 !important`,
                }}
                sub={<Data>{data.blurb}</Data>}
              />
            </div>
          </BootcampGridLeft>
          <BootcampGridRight>
            {data.upcoming?.length > 0 ? (
              <div
                css={{
                  display: 'grid',
                  gap: theme.space.base,
                }}
              >
                {data.upcoming.map((bootcamp) => (
                  <DateActionCard
                    key={`${bootcamp.date} ${bootcamp.heading}`}
                    date={bootcamp.date}
                    time={bootcamp.time}
                    title={bootcamp.location}
                    actionProps={{
                      size: 'xsmall',
                      url: bootcamp.link,
                    }}
                    actionLabel="Register now"
                  />
                ))}
              </div>
            ) : (
              <p css={{ textAlign: 'center', color: theme.fontColor.muted }}>
                There are no upcoming Bootcamps at this time. Please check back
                shortly!
              </p>
            )}
          </BootcampGridRight>
        </BootcampGrid>
      </Container>
    </Section>
  )
}

BootcampFindSection.propTypes = {
  data: PropTypes.object.isRequired,
}

const bootcampRequestSchema = yup.object().shape({
  'bot-field': yup.string().max(0),
  name: yup.string().required().trim().label('Name'),
  company: yup.string().required().trim().label('Company'),
  jobTitle: yup.string().required().trim().label('Job title'),
  email: yup
    .string()
    .required()
    .email('Please provide a valid email')
    .corporateEmail()
    .trim()
    .label('Email'),
  purpose: yup
    .string()
    .required()
    .trim()
    .oneOf(['Conference', 'CommunityEvent', 'Company/Team'])
    .label('Purpose'),
  location: yup
    .string()
    .required()
    .trim()
    .oneOf(['In-person', 'Virtual'])
    .label('Location'),
  date: yup.string().trim().label('Date'),
  message: yup.string().trim().label('Message'),
})

const BootcampFormSection = ({ data }) => {
  const theme = useTheme()

  const [response, setResponse] = useState()

  const formId = 'bootcamp-request'

  const initialValues = {
    'form-name': formId,
    'bot-field': '',
    name: '',
    company: '',
    jobTitle: '',
    email: '',
    purpose: '',
    location: '',
    date: '',
    message: '',
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    setResponse(null)
    setSubmitting(true)

    const [, success] = await to(
      axios({
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        data: qs.stringify(values),
        url: '/',
      })
    )

    setSubmitting(false)

    if (success) {
      setResponse('success')

      track('Contact requested', {
        label: `netlify-${formId}`,
      })
    } else {
      setResponse('error')
    }
  }

  return (
    <Section
      css={{
        background: generateGradient({
          start: theme.brand.darkPurple,
          stop: theme.brand.purple,
        }),
      }}
      id="request"
      className="u-contrast"
    >
      <Container>
        <BootcampGrid>
          <BootcampGridLeft>
            <div
              css={{
                [theme.mq.medium]: [stickyIt(theme)],
              }}
            >
              <SectionTitle
                as="h2"
                align={{ medium: 'left' }}
                title={<Data>{data.heading}</Data>}
                sub={<Data>{data.blurb}</Data>}
              />
            </div>
          </BootcampGridLeft>
          <BootcampGridRight>
            {response === 'success' && (
              <Alert type="primary">
                Thanks for your submission! We'll be in touch soon!
              </Alert>
            )}

            {response === 'error' && (
              <Alert type="danger">
                There was an error submitting your subscription. Please try
                again later.
              </Alert>
            )}
            {!response && (
              <Formik
                initialValues={initialValues}
                validationSchema={bootcampRequestSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting }) => (
                  <Form
                    name={formId}
                    data-netlify="true"
                    data-netlify-honeypot="bot-field"
                    noValidate
                  >
                    <input type="hidden" name="form-name" value={formId} />
                    <BotField name="bot-field" css={misc.hide} type="text" />
                    <div
                      css={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr)',
                        gap: theme.space.base,
                      }}
                    >
                      <FormikField
                        required
                        name="name"
                        as="input"
                        label="Name"
                      />
                      <FormikField
                        required
                        name="company"
                        as="input"
                        label="Company"
                      />
                      <FormikField
                        required
                        name="jobTitle"
                        as="input"
                        label="Job title"
                      />
                      <FormikField
                        required
                        name="email"
                        as="input"
                        type="email"
                        label="Email"
                      />
                      <FormikField
                        required
                        name="purpose"
                        as="select"
                        label="What is this Bootcamp for?"
                      >
                        <option value="">---</option>
                        <option value="Conference">Conference</option>
                        <option value="Community Event">Community Event</option>
                        <option value="Company/Team">Company/Team</option>
                      </FormikField>
                      <FormikField
                        required
                        name="location"
                        as="select"
                        label="Location"
                      >
                        <option value="">---</option>
                        <option value="In-person">In-person</option>
                        <option value="Virtual">Virtual</option>
                      </FormikField>
                      <FormikField
                        name="date"
                        as="input"
                        type="date"
                        label="Date (if applicable)"
                      />
                      <FormikField
                        name="message"
                        as="textarea"
                        label="Anything else we should know?"
                      />
                    </div>
                    <Button
                      loading={isSubmitting}
                      actionType="submit"
                      type="primary"
                      noContrast
                      css={{ marginTop: theme.space.large }}
                    >
                      Submit
                    </Button>
                  </Form>
                )}
              </Formik>
            )}
          </BootcampGridRight>
        </BootcampGrid>
      </Container>
    </Section>
  )
}

BootcampFormSection.propTypes = {
  data: PropTypes.object.isRequired,
}

const conferenceLogos = [
  {
    width: 150,
    src: conferenceAWSReinvent,
    name: 'AWS re:Invent',
  },
  {
    width: 100,
    src: conferenceSRECon,
    name: 'SRE Con',
  },
  {
    width: 140,
    src: conferenceQCon,
    name: 'QCon',
  },
  {
    width: 120,
    src: conferenceChaosConf,
    name: 'Chaos Conf',
  },
]

const BootcampLogosSections = ({ data }) => {
  const theme = useTheme()

  return (
    <Fragment>
      <Section>
        <Container>
          <BootcampGrid>
            <BootcampGridLeft>
              <SectionTitle
                as="h2"
                align={{ medium: 'left' }}
                title={<Data>{data.customers.heading}</Data>}
                sub={<Data>{data.customers.blurb}</Data>}
              />
            </BootcampGridLeft>
            <BootcampGridRight>
              <LogosGrid
                logos={[expediaObj, workivaObj, jpmcObj]}
                css={{ background: theme.brand.purple }}
              />
            </BootcampGridRight>
          </BootcampGrid>
        </Container>
      </Section>
      <Section>
        <Container>
          <BootcampGrid>
            <BootcampGridLeft>
              <LogosGrid
                logos={conferenceLogos}
                css={{ background: theme.color.primary.base }}
              />
            </BootcampGridLeft>
            <BootcampGridRight>
              <SectionTitle
                as="h2"
                align={{ medium: 'left' }}
                title={<Data>{data.conferences.heading}</Data>}
                sub={<Data>{data.conferences.blurb}</Data>}
              />
            </BootcampGridRight>
          </BootcampGrid>
        </Container>
      </Section>
    </Fragment>
  )
}

BootcampLogosSections.propTypes = {
  data: PropTypes.object.isRequired,
}

const BootcampsFooterGridItem = ({ src, variants, controls, ...rest }) => (
  <motion.div
    initial="hidden"
    variants={variants}
    animate={controls}
    css={{
      paddingBottom: '56.25%', // 3:2 aspect ratio
      background: `url(${src}) no-repeat center center`,
      backgroundSize: 'cover',
    }}
    {...rest}
  />
)

BootcampsFooterGridItem.propTypes = {
  controls: PropTypes.object.isRequired,
  src: PropTypes.string.isRequired,
  variants: PropTypes.object.isRequired,
}

const BootcampsFooter = () => {
  const theme = useTheme()

  const footerRef = useRef()
  const visible = useRef(false)

  const controls = useAnimation()

  const variants = {
    hidden: {
      opacity: 0,
      scale: 0.5,
    },
    visible: (i) => ({
      opacity: 1,
      scale: 1,
      transition: {
        delay: i * theme.motion.timing.short,
      },
    }),
  }

  const intersection = useIntersection(footerRef, {
    rootMargin: '0px',
    threshold: 0.25,
  })

  useEffect(() => {
    if (intersection?.isIntersecting && !visible.current) {
      visible.current = true

      controls.start('visible')
    }
  }, [intersection, controls])

  return (
    <div ref={footerRef}>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 0.5fr',
          gap: theme.space.xsmall,
          marginBottom: theme.space.xsmall,
        }}
      >
        <BootcampsFooterGridItem
          controls={controls}
          variants={variants}
          custom={1}
          src={footer1}
        />
        <BootcampsFooterGridItem
          controls={controls}
          variants={variants}
          custom={2}
          src={footer2}
        />
        <BootcampsFooterGridItem
          controls={controls}
          variants={variants}
          custom={3}
          src={footer3}
        />
        <BootcampsFooterGridItem
          controls={controls}
          variants={variants}
          custom={4}
          src={footer2}
          css={{ backgroundPosition: 'left center' }}
        />
      </div>
      <div
        css={{
          display: 'grid',
          gap: theme.space.xsmall,
          gridTemplateColumns: '0.5fr 1fr 1fr 1fr',
          marginBottom: theme.space.xsmall,
        }}
      >
        <BootcampsFooterGridItem
          src={footer2}
          css={{ backgroundPosition: 'right center' }}
          controls={controls}
          variants={variants}
          custom={5}
        />
        <BootcampsFooterGridItem
          src={footer4}
          controls={controls}
          variants={variants}
          custom={6}
        />
        <BootcampsFooterGridItem
          src={footer5}
          controls={controls}
          variants={variants}
          custom={7}
        />
        <BootcampsFooterGridItem
          src={footer6}
          controls={controls}
          variants={variants}
          custom={8}
        />
      </div>
    </div>
  )
}

const Bootcamps = ({
  data: {
    file: {
      childMdx: { frontmatter: pageData },
    },
  },
}) => {
  const theme = useTheme()

  return (
    <BaseLayout footerProps={{ hideCTA: true }}>
      <HelmetSEO title={pageData.title} description={pageData.description} />
      <Hero
        css={{
          background: generateGradient({
            start: theme.brand.darkPurple,
            stop: theme.brand.purple,
          }),
          paddingBottom: 0,
        }}
      >
        <HomeHeroGrid>
          <HomeHeroGridImage
            css={{
              [theme.mq.small__max]: {
                textAlign: 'center',
              },

              [theme.mq.medium]: {
                alignSelf: 'end',
              },
            }}
          >
            <HeroSVG
              css={{
                [theme.mq.small__max]: {
                  maxWidth: 400,
                },
              }}
            />
          </HomeHeroGridImage>
          <HomeHeroGridContent
            css={{
              paddingBottom: theme.space.large,

              [theme.mq.medium]: {
                gridColumn: '1 / span 7',
                paddingBottom: 0,
              },
            }}
          >
            <HeroTitle size="large">
              <Data>{pageData.hero.heading}</Data>
            </HeroTitle>
            <h4
              css={{
                textTransform: 'uppercase',
                letterSpacing: theme.letterSpacing.extended,
                marginTop: 0,
                color: `${theme.color.primary.base} !important`,
              }}
            >
              <Data>{pageData.hero.meta}</Data>
            </h4>
            <HeroTitleSub>
              <Data>{pageData.hero.blurb}</Data>
            </HeroTitleSub>
            <div css={{ marginTop: theme.space.large }}>
              <Inline
                css={{
                  justifyContent: 'center',

                  [theme.mq.medium]: {
                    justifyContent: 'flex-start',
                  },
                }}
              >
                <ListItem>
                  <Button noContrast type="primary" url="#find">
                    Find a Bootcamp
                  </Button>
                </ListItem>
              </Inline>
            </div>
          </HomeHeroGridContent>
        </HomeHeroGrid>
      </Hero>
      <BootcampWorkshopsSection data={[pageData.bootcamps101]} />
      <BootcampFindSection data={pageData.bootcampsFind} />
      <BootcampLogosSections
        data={{
          customers: pageData.bootcampsCustomers,
          conferences: pageData.bootcampsConferences,
        }}
      />
      <BootcampsFooter />
    </BaseLayout>
  )
}

Bootcamps.propTypes = {
  data: PropTypes.object.isRequired,
}

export default Bootcamps

export const pageQuery = graphql`
  query BootcampsPageData {
    file(name: { eq: "bootcamps" }, absolutePath: { regex: "/src/data/" }) {
      childMdx {
        frontmatter {
          title
          description
          hero {
            blurb
            heading
            meta
          }
          bootcamps101 {
            heading
            blurb
            agenda {
              blurb
            }
          }
          bootcampsFind {
            heading
            blurb
            upcoming {
              date
              time
              location
              link
            }
          }
          bootcampsCustomers {
            heading
            blurb
          }
          bootcampsConferences {
            heading
            blurb
          }
        }
      }
    }
  }
`
